import "./fuyaozi-hexagram-style.scss";

//—— 卦象圖 ——
//
//  斷語訊息最上面那一塊：卦名 + 六爻的圖示。
//
//  —— 這一塊完全不經過 AI ——
//  卦名、陰陽、動爻、世應都是排盤器算出來的事實。交給模型「描述」一次
//  等於給它一個寫錯的機會，而這是整則訊息裡最不該錯的地方。
//
//  —— 跟搖龜殼那一幕的差別 ——
//  那裡**不顯示世應**，因為世應要先知道完整六爻才算得出來，提早標出來
//  等於自曝卦早就擲完了。這裡六爻都亮完了，標出來才是完整的卦象。

//爻的稱呼。
//
//  用「初爻、二爻…六爻」而不是周易的爻題（初九、六五）——
//  那一套要先懂「九是陽、六是陰」才讀得出位置，對一般使用者是門檻。
//  （Kevin 2026-10-08 決定。）
//
//  ⚠️ 餵給 AI 的 prompt 仍然用初九／六五那一套，不要跟著改 ——
//  那是為了讓它引對爻辭用的（《周易》的原文是按爻題編的），
//  改成「四爻」它就查不到「六四：安節，亨」那一條了。
const YAO_NAMES = ["初爻", "二爻", "三爻", "四爻", "五爻", "六爻"];

//  gua  排盤器的 buildGua() 結果
const FuyaoziHexagram = ({ gua }) => {
  const rows = gua?.rows || [];
  if (!rows.length) return null;

  const { original, changed, movings = [], time = {} } = gua;

  //—— 卦象的基本資料 ——
  //  兩行，照 Kevin 2026-10-08 給的格式：
  //    離宮。上離 下巽。 丙午年 丁酉月 乙卯日
  //    無動爻。空亡：子、丑
  const movingText = movings.length
    ? `${movings.map((n) => YAO_NAMES[n - 1]).join("、")}動`
    : "無動爻";

  return (
    <div className="fuyaozi-hexagram">
      <div className="fuyaozi-hexagram__names">
        <span className="fuyaozi-hexagram__name">{original.name}</span>
        {changed && (
          <>
            <i className="fuyaozi-hexagram__arrow" aria-hidden="true">
              →
            </i>
            <span className="fuyaozi-hexagram__name is-changed">
              {changed.name}
            </span>
          </>
        )}
      </div>

      <p className="fuyaozi-hexagram__meta">
        {original.palace}宮。上{original.upper} 下{original.lower}。{" "}
        {time.year}年 {time.month}月 {time.day}日
        <br />
        {movingText}
        {/* 空亡是起卦時才算得出來的，xunKong 是後來才加進排盤器的 ——
            在那之前存下來的卜卦沒有這個欄位，整句省掉比印一個空的「空亡：」好。 */}
        {time.xunKong?.length ? `。空亡：${time.xunKong.join("、")}` : ""}
      </p>

      {/* column-reverse：DOM 第一個是初爻、畫出來在最底。
          六爻的卦圖由下往上讀，畫反了是硬錯。 */}
      <ol className="fuyaozi-hexagram__yaos">
        {rows.map((row, i) => (
          <li
            key={row.position}
            className={`fuyaozi-hexagram__row${row.moving ? " is-moving" : ""}`}
          >
            <span className="fuyaozi-hexagram__title">{YAO_NAMES[i]}</span>

            {/* 六神。依起卦日的日干起（青龍朱雀勾陳騰蛇白虎玄武），
                斷卦時用來看事情的性質（白虎主凶傷、青龍主喜慶…）。 */}
            <span className="fuyaozi-hexagram__god">{row.god}</span>

            {/* 陽爻一整條、陰爻斷成兩截。
                陽爻用單一元素 —— 兩個各自有圓角的方塊接在中間會有接縫。 */}
            <span
              className={`fuyaozi-hexagram__bar${row.yang ? " is-yang" : " is-yin"}`}
            >
              <i />
              {!row.yang && <i />}
            </span>

            {/* 動爻的記號：動陽 ○、動陰 ×（六爻的傳統畫法） */}
            <span className="fuyaozi-hexagram__mark">
              {row.moving ? (row.yang ? "○" : "×") : ""}
            </span>

            <span className="fuyaozi-hexagram__info">
              {row.isWorld ? "世" : row.isResponse ? "應" : ""}
            </span>

            {/* 本卦、化爻、伏神排成一行，中間用半形空白隔開（Kevin 2026-10-08）。
                三段各自 nowrap —— CJK 預設哪裡都能斷，不鎖的話會斷成
                「寅木官」／「鬼 伏巳火父母」。鎖住之後唯一能折的地方就是
                那兩個空白，窄螢幕上頂多把化爻或伏神換到下一行，不會切半個詞。 */}
            <span className="fuyaozi-hexagram__text">
              <span className="fuyaozi-hexagram__self">
                {row.branch}
                {row.element}
                {row.relative}
              </span>

              {/* 動爻化出來的那一支 */}
              {row.moving && row.changed && (
                <>
                  {" "}
                  <i className="fuyaozi-hexagram__to">
                    化{row.changed.branch}
                    {row.changed.element}
                    {row.changed.relative}
                  </i>
                </>
              )}

              {/* 伏神：這一卦缺的六親藏在本宮卦的同一爻底下 */}
              {row.hidden && (
                <>
                  {" "}
                  <i className="fuyaozi-hexagram__hidden">
                    伏{row.hidden.branch}
                    {row.hidden.element}
                    {row.hidden.relative}
                  </i>
                </>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default FuyaoziHexagram;
